import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';

function iniciais(nome) {
  const p = String(nome || '').trim().split(/\s+/).slice(0, 2);
  return p.map((x) => x[0]?.toUpperCase()).join('') || '?';
}
function hora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
const CORES = ['#57A94F', '#F5A623', '#4CB5C0', '#B07BCC', '#E5748B', '#6B9BE5'];
function corDoNome(nome) {
  let h = 0;
  const s = String(nome || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return CORES[h % CORES.length];
}

// Chat interno entre funcionários do MESMO canal (não é WhatsApp).
// Cada canal tem: um GRUPO (todos) + conversas PRIVADAS (DM) com cada membro.
export default function ChatInternoModal({ aberto, onClose, onLido }) {
  const { user } = useAuth();
  const meuId = Number(user?.id);
  const [canais, setCanais] = useState([]);
  const [canalId, setCanalId] = useState(null);
  const [membros, setMembros] = useState([]);
  const [ativo, setAtivo] = useState(null); // null (lista) | {tipo:'grupo'} | {tipo:'dm', id, nome}
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [naoLidas, setNaoLidas] = useState({}); // { "canalId:comId": total } — comId 0 = grupo
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const ultimoMarcadoRef = useRef(0);

  // Não-lidas por conversa (pros badges vermelhos na lista).
  const carregarNaoLidas = () => {
    axios.get(`${API}/chat-interno/nao-lidas`).then((r) => {
      const map = {};
      (r.data?.data?.porConversa || []).forEach((x) => {
        map[`${x.canal_id}:${x.com_id}`] = Number(x.total || 0);
      });
      setNaoLidas(map);
    }).catch(() => {});
  };

  // Canais ao abrir
  useEffect(() => {
    if (!aberto) return;
    axios.get(`${API}/chat-interno/canais`).then((r) => {
      const cs = r.data?.data || [];
      setCanais(cs);
      setCanalId((prev) => prev || cs[0]?.id || null);
    }).catch(() => {});
  }, [aberto]);

  // Membros do canal ativo + volta pra lista ao trocar de canal
  useEffect(() => {
    if (!aberto || !canalId) return;
    setAtivo(null);
    axios.get(`${API}/chat-interno/${canalId}/membros`).then((r) => setMembros(r.data?.data || [])).catch(() => {});
  }, [aberto, canalId]);

  // Não-lidas enquanto o modal está aberto (badges na lista, atualiza sozinho).
  useEffect(() => {
    if (!aberto) return undefined;
    carregarNaoLidas();
    const t = setInterval(carregarNaoLidas, 10000);
    return () => clearInterval(t);
  }, [aberto, canalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mensagens da conversa ativa (grupo ou DM) + polling. Abrir/receber = marcar lida.
  useEffect(() => {
    if (!aberto || !canalId || !ativo) { setMensagens([]); return undefined; }
    ultimoMarcadoRef.current = 0; // conversa nova: libera marcar de novo
    const com = ativo.tipo === 'dm' ? ativo.id : 'grupo';
    const marcar = (msgs) => {
      const maxId = msgs.reduce((mx, m) => (typeof m.id === 'number' && m.id > mx ? m.id : mx), 0);
      if (!maxId || maxId <= ultimoMarcadoRef.current) return;
      ultimoMarcadoRef.current = maxId;
      axios.post(`${API}/chat-interno/${canalId}/lida`, { ate: maxId }, { params: { com } })
        .then(() => { carregarNaoLidas(); onLido?.(); }).catch(() => {});
    };
    const carregar = () => axios.get(`${API}/chat-interno/${canalId}/mensagens`, { params: { com } })
      .then((r) => { const d = r.data?.data || []; setMensagens(d); marcar(d); }).catch(() => {});
    carregar();
    pollRef.current = setInterval(carregar, 4000);
    return () => clearInterval(pollRef.current);
  }, [aberto, canalId, ativo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensagens.length, ativo]);

  const enviar = async (e) => {
    e?.preventDefault?.();
    const t = texto.trim();
    if (!t || !canalId || !ativo || enviando) return;
    setEnviando(true);
    const dest = ativo.tipo === 'dm' ? ativo.id : null;
    const temp = {
      id: `temp-${Date.now()}`, usuario_id: meuId, usuario_nome: user?.usuario,
      destinatario_id: dest, mensagem: t, criado_em: new Date().toISOString(),
    };
    setMensagens((m) => [...m, temp]);
    setTexto('');
    try {
      await axios.post(`${API}/chat-interno/${canalId}/mensagens`, { mensagem: t, destinatario_id: dest });
      const com = ativo.tipo === 'dm' ? ativo.id : 'grupo';
      const r = await axios.get(`${API}/chat-interno/${canalId}/mensagens`, { params: { com } });
      setMensagens(r.data?.data || []);
    } catch (err) {
      setMensagens((m) => m.filter((x) => x.id !== temp.id));
      setTexto((cur) => cur || t);
    } finally {
      setEnviando(false);
    }
  };

  if (!aberto) return null;

  const tituloConversa = ativo?.tipo === 'grupo' ? 'Grupo do canal' : (ativo?.nome || 'Conversa');
  const badge = (n) => (n > 0 ? (
    <span style={{
      minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: '#e5484d', color: '#fff',
      fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
      lineHeight: 1, flexShrink: 0,
    }}>{n > 99 ? '99+' : n}</span>
  ) : null);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="content-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(480px, 96vw)', height: 'min(78vh, 640px)', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
      >
        {/* Header */}
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, padding: '12px 14px', borderBottom: '1px solid var(--portal-border)' }}>
          {ativo ? (
            <button type="button" onClick={() => setAtivo(null)} aria-label="Voltar" style={{ background: 'transparent', border: 'none', color: 'var(--portal-text)', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}>
              <i className="bi bi-arrow-left" />
            </button>
          ) : (
            <i className="bi bi-people-fill" style={{ color: '#3D8B37', fontSize: '1.2rem' }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: '0.95rem' }}>{ativo ? tituloConversa : 'Chat interno'}</strong>
            {!ativo && <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>Equipe do canal — não vai pro cliente.</div>}
          </div>
          {!ativo && canais.length > 1 && (
            <select value={canalId || ''} onChange={(ev) => setCanalId(Number(ev.target.value))} style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.8rem' }}>
              {canais.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          )}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </header>

        {/* Corpo */}
        {!ativo ? (
          // LISTA: Grupo (fixado) + membros
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <button type="button" onClick={() => setAtivo({ tipo: 'grupo' })} style={listaItemStyle}>
              <div style={{ ...avatarStyle, background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)' }}>
                <i className="bi bi-megaphone-fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: '0.9rem' }}>Grupo do canal</strong>
                <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>Todos os funcionários deste canal</div>
              </div>
              {badge(naoLidas[`${canalId}:0`])}
              <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)' }} />
            </button>
            <div style={{ padding: '8px 14px 4px', fontSize: '0.7rem', color: 'var(--portal-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Conversas privadas
            </div>
            {membros.length === 0 ? (
              <div style={{ padding: 14, fontSize: '0.82rem', color: 'var(--portal-muted)' }}>Ninguém mais neste canal.</div>
            ) : membros.map((m) => (
              <button key={m.id} type="button" onClick={() => setAtivo({ tipo: 'dm', id: m.id, nome: m.nome })} style={listaItemStyle}>
                <div style={{ ...avatarStyle, background: corDoNome(m.nome) }}>{iniciais(m.nome)}</div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <strong style={{ fontSize: '0.9rem' }}>{m.nome}</strong>
                </div>
                {badge(naoLidas[`${canalId}:${m.id}`])}
                <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)' }} />
              </button>
            ))}
          </div>
        ) : (
          // CONVERSA
          <>
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, background: 'var(--portal-chat-bg)' }}>
              {mensagens.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem', marginTop: 20 }}>
                  {ativo.tipo === 'grupo' ? 'Comece a conversa com a equipe.' : `Sem mensagens com ${tituloConversa} ainda.`}
                </div>
              ) : mensagens.map((m) => {
                const meu = Number(m.usuario_id) === meuId;
                return (
                  <div key={m.id} style={{ display: 'flex', justifyContent: meu ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                    <div style={{
                      maxWidth: '80%', padding: '7px 11px', borderRadius: 12,
                      borderTopRightRadius: meu ? 4 : 12, borderTopLeftRadius: meu ? 12 : 4,
                      background: meu ? 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)' : 'var(--portal-bubble-in)',
                      color: meu ? '#fff' : 'var(--portal-text)', fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {!meu && ativo.tipo === 'grupo' && (
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: corDoNome(m.usuario_nome), marginBottom: 1 }}>{m.usuario_nome || 'colega'}</div>
                      )}
                      <div>{m.mensagem}</div>
                      <div style={{ fontSize: '0.66rem', opacity: 0.7, textAlign: 'right', marginTop: 2 }}>{hora(m.criado_em)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={enviar} style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--portal-border)', flexShrink: 0 }}>
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={ativo.tipo === 'grupo' ? 'Mensagem pra equipe...' : `Mensagem pra ${tituloConversa}...`}
                rows={1}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(e); } }}
                style={{ flex: 1, resize: 'none', minHeight: 38, maxHeight: 120, padding: '8px 12px', borderRadius: 10, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.9rem' }}
              />
              <button type="submit" disabled={enviando || !texto.trim()} aria-label="Enviar" style={{ background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', opacity: (enviando || !texto.trim()) ? 0.55 : 1 }}>
                <i className="bi bi-send-fill" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const listaItemStyle = {
  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
  padding: '11px 14px', background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--portal-border)', cursor: 'pointer', color: 'var(--portal-text)',
};
const avatarStyle = {
  width: 40, height: 40, borderRadius: '50%', flexShrink: 0, color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.85rem',
};
